import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, AlertCircle, AlertTriangle, Info, CheckCircle, ExternalLink, Megaphone } from 'lucide-react';

const AdminBannersTab = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    audience: 'both',
    placement: 'dashboard_only',
    cta_text: '',
    cta_link: '',
    enabled: true,
    start_date: '',
    end_date: '',
    dismissible: false
  });

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      audience: 'both',
      placement: 'dashboard_only',
      cta_text: '',
      cta_link: '',
      enabled: true,
      start_date: '',
      end_date: '',
      dismissible: false
    });
    setEditingBanner(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setFormData({
      title: banner.title || '',
      message: banner.message || '',
      type: banner.type || 'info',
      audience: banner.audience || 'both',
      placement: banner.placement || 'dashboard_only',
      cta_text: banner.cta_text || '',
      cta_link: banner.cta_link || '',
      enabled: banner.enabled ?? true,
      start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
      end_date: banner.end_date ? banner.end_date.split('T')[0] : '',
      dismissible: banner.dismissible ?? false
    });
    setEditingBanner(banner);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        title: formData.title || null,
        cta_text: formData.cta_text || null,
        cta_link: formData.cta_link || null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner updated successfully');
      } else {
        await axios.post(`${API}/admin/banners`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error(error.response?.data?.detail || 'Failed to save banner');
    }
  };

  const handleToggle = async (bannerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/admin/banners/${bannerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast.error('Failed to toggle banner');
    }
  };

  const handleDelete = async (bannerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted successfully');
      setDeleteConfirmId(null);
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Failed to delete banner');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'critical': return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />;
      default: return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-amber-100 text-amber-800';
      case 'success': return 'bg-green-100 text-green-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getAudienceBadgeColor = (audience) => {
    switch (audience) {
      case 'providers': return 'bg-purple-100 text-purple-800';
      case 'customers': return 'bg-cyan-100 text-cyan-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-emerald-600" />
              Banners
            </CardTitle>
            <CardDescription>
              Manage in-app announcements and alerts for users. Changes take effect immediately.
            </CardDescription>
          </div>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Banner
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {banners.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Megaphone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No banners created yet</p>
            <p className="text-sm mt-2">Create your first banner to start communicating with users</p>
          </div>
        ) : (
          <div className="space-y-4">
            {banners.map((banner) => (
              <div 
                key={banner.id} 
                className={`p-4 rounded-lg border ${banner.enabled ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getTypeIcon(banner.type)}
                      <span className="font-medium text-gray-900">
                        {banner.title || 'Untitled Banner'}
                      </span>
                      <Badge className={`text-xs ${getTypeBadgeColor(banner.type)}`}>
                        {banner.type}
                      </Badge>
                      <Badge className={`text-xs ${getAudienceBadgeColor(banner.audience)}`}>
                        {banner.audience}
                      </Badge>
                      {banner.dismissible && (
                        <Badge variant="outline" className="text-xs">dismissible</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{banner.message}</p>
                    {banner.cta_text && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <ExternalLink className="h-3 w-3" />
                        CTA: {banner.cta_text} → {banner.cta_link}
                      </div>
                    )}
                    {(banner.start_date || banner.end_date) && (
                      <div className="text-xs text-gray-500 mt-1">
                        Schedule: {banner.start_date ? new Date(banner.start_date).toLocaleDateString() : 'No start'} 
                        {' → '}
                        {banner.end_date ? new Date(banner.end_date).toLocaleDateString() : 'No end'}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(banner.id)}
                      title={banner.enabled ? 'Disable' : 'Enable'}
                    >
                      {banner.enabled ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(banner)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(banner.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBanner ? 'Edit Banner' : 'Create Banner'}</DialogTitle>
              <DialogDescription>
                {editingBanner 
                  ? 'Update banner content. Changes take effect immediately.'
                  : 'Create a new banner to display to users.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="title">Title (optional)</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Banner title"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Banner message content"
                    required
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="audience">Audience</Label>
                    <Select value={formData.audience} onValueChange={(v) => setFormData({...formData, audience: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Both</SelectItem>
                        <SelectItem value="providers">Providers Only</SelectItem>
                        <SelectItem value="customers">Customers Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="cta_text">CTA Button Text (optional)</Label>
                  <Input
                    id="cta_text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                    placeholder="e.g., Learn More"
                  />
                </div>

                <div>
                  <Label htmlFor="cta_link">CTA Link (optional)</Label>
                  <Input
                    id="cta_link"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({...formData, cta_link: e.target.value})}
                    placeholder="e.g., /payments or https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Internal route (e.g., /payments) or external URL</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date (optional)</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="end_date">End Date (optional)</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enabled"
                      checked={formData.enabled}
                      onCheckedChange={(checked) => setFormData({...formData, enabled: checked})}
                    />
                    <Label htmlFor="enabled">Enabled</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="dismissible"
                      checked={formData.dismissible}
                      onCheckedChange={(checked) => setFormData({...formData, dismissible: checked})}
                    />
                    <Label htmlFor="dismissible">Dismissible</Label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  {editingBanner ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Banner</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this banner? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(deleteConfirmId)}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminBannersTab;
